import React, { useMemo, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import styles from '../styles/Live.module.css';
import { seriesColor } from '../lib/palette';

/**
 * LiveTelemetry
 *
 * Streaming-friendly telemetry visualization. The classic "full telemetry"
 * page compares two laps end-to-end; that model doesn't work live because
 * there's no lap boundary yet. So instead we show a rolling ~30-second
 * window of each channel, with all selected drivers overlaid on the same
 * time axis so you can see head-to-head deltas as they happen.
 *
 * Tiles:
 *   ┌───────────────┐  ┌────────┐  ┌──────┐
 *   │     Speed     │  │  RPM   │  │ Gear │
 *   ├───────────────┤  ├────────┤  ├──────┤
 *   │ Throttle+Brake│  │  DRS   │  │      │
 *   └───────────────┘  └────────┘  └──────┘
 *
 * Props:
 *   selected: [driverNumber, ...]          up to 4
 *   drivers:  metadata keyed by number     { number: {tla, color, speed, rpm, ...} }
 *   telemetry: { number: { seq, samples: [{t, speed, rpm, gear, throttle, brake, drs}, ...] } }
 *
 * The parent manages SSE subscription; this component just renders what it
 * gets and maintains its own per-driver rolling buffer so old samples age
 * out of the window smoothly.
 */
const WINDOW_MS = 30_000;
const MAX_POINTS = 200;
const CHANNELS = ['speed', 'rpm', 'gear', 'throttle', 'brake', 'drs'];

function parseSampleTime(t) {
  if (t == null) return null;
  if (typeof t === 'number') return t * 1000;
  // ISO string
  const d = new Date(t);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Accumulates streaming samples per driver into a fixed rolling window,
 * anchored on the LATEST sample timestamp across all selected drivers
 * rather than wall clock. This is important because replay mode feeds
 * timestamps from the original session (which could be months or years
 * old); comparing to Date.now() would push every sample off-chart.
 */
function useRollingBuffer(telemetry, drivers, selected) {
  const bufRef = useRef({});       // { num: [{t, speed, ...}] }

  // merge new samples
  if (telemetry) {
    for (const num of Object.keys(telemetry)) {
      const bundle = telemetry[num];
      if (!bundle?.samples?.length) continue;
      if (!bufRef.current[num]) bufRef.current[num] = [];
      for (const s of bundle.samples) {
        const ms = parseSampleTime(s.t);
        if (ms == null) continue;
        bufRef.current[num].push({ ...s, ms });
      }
    }
  }

  // drop drivers that got unselected BEFORE finding the anchor so stale
  // buffers from deselected drivers don't drag the anchor backward
  for (const num of Object.keys(bufRef.current)) {
    if (!selected.includes(num)) delete bufRef.current[num];
  }

  // anchor "now" on the most recent sample we've seen across any selected
  // driver — keeps the rolling window meaningful regardless of timestamp epoch
  let anchor = 0;
  for (const num of selected) {
    const arr = bufRef.current[num];
    if (!arr || !arr.length) continue;
    const last = arr[arr.length - 1].ms;
    if (last > anchor) anchor = last;
  }
  if (!anchor) return [];

  // prune and trim against anchor
  const cutoff = anchor - WINDOW_MS;
  for (const num of Object.keys(bufRef.current)) {
    const arr = bufRef.current[num];
    let i = 0;
    while (i < arr.length && arr[i].ms < cutoff) i++;
    if (i > 0) arr.splice(0, i);
    if (arr.length > MAX_POINTS) arr.splice(0, arr.length - MAX_POINTS);
  }

  // build combined series keyed on seconds relative to the anchor (≤ 0)
  const combined = [];
  const byT = new Map();
  for (const num of selected) {
    const arr = bufRef.current[num];
    if (!arr) continue;
    for (const s of arr) {
      const tRel = (s.ms - anchor) / 1000; // 0 on the right, −WINDOW_MS/1000 on the left
      const tKey = Math.round(tRel * 10) / 10;
      let row = byT.get(tKey);
      if (!row) {
        row = { t: tKey };
        byT.set(tKey, row);
      }
      for (const ch of CHANNELS) {
        if (s[ch] != null) row[`${num}_${ch}`] = s[ch];
      }
    }
  }
  const keys = Array.from(byT.keys()).sort((a, b) => a - b);
  for (const k of keys) combined.push(byT.get(k));

  return combined;
}

const GRID = { stroke: 'rgba(255,255,255,0.05)' };
const TICK = { fill: '#8c8c8c', fontFamily: "var(--mono)", fontSize: 9 };

function ChannelTile({ title, dataKey, domain, formatter, data, selected, drivers }) {
  return (
    <div className={styles.telTile}>
      <div className={styles.telLabel}>{title}</div>
      <div className={styles.telTileChart}>
        <ResponsiveContainer width="100%" height="100%" minHeight={80}>
          <LineChart data={data} margin={{ top: 4, right: 8, left: -4, bottom: 2 }}>
            <CartesianGrid {...GRID} />
            <XAxis
              dataKey="t"
              type="number"
              domain={[-WINDOW_MS / 1000, 0]}
              tick={TICK}
              tickFormatter={v => `${v.toFixed(0)}s`}
              allowDataOverflow={true}
            />
            <YAxis tick={TICK} domain={domain} tickFormatter={formatter} width={32} />
            <Tooltip
              contentStyle={{ background: '#080808', border: '1px solid #222', fontFamily: "var(--mono)", fontSize: '0.6rem' }}
              labelFormatter={v => `${v.toFixed(1)}s ago`}
              formatter={(v, name) => {
                const num = name.split('_')[0];
                const d = drivers?.[num];
                const tla = d?.tla || num;
                return [formatter ? formatter(v) : v, tla];
              }}
            />
            {selected.map(num => {
              const d = drivers?.[num];
              const key = `${num}_${dataKey}`;
              return (
                <Line
                  key={key}
                  dataKey={key}
                  stroke={seriesColor(d)}
                  dot={false}
                  strokeWidth={1.5}
                  isAnimationActive={false}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function LiveTelemetry({ selected, drivers, telemetry, allDrivers, onToggle, onSelectAll, onClearAll }) {
  const combined = useRollingBuffer(telemetry, drivers, selected);

  // driver chip row — all drivers, top 20 by current position
  const chipDrivers = useMemo(() => {
    if (!allDrivers) return [];
    return allDrivers.slice(0, 20);
  }, [allDrivers]);

  const allSelected = chipDrivers.length > 0 && selected.length >= chipDrivers.length;

  return (
    <div className={`${styles.panel} ${styles.telemetryPanel}`}>
      <div className={styles.panelHeader}>Telemetry</div>
      <div className={styles.telHeader}>
        <div className={styles.driverChips}>
          <button
            className={`${styles.driverChipMeta} ${allSelected ? styles.driverChipMetaDisabled : ''}`}
            onClick={() => onSelectAll?.()}
            disabled={allSelected}
            title="Select all drivers"
          >
            ALL
          </button>
          <button
            className={`${styles.driverChipMeta} ${selected.length === 0 ? styles.driverChipMetaDisabled : ''}`}
            onClick={() => onClearAll?.()}
            disabled={selected.length === 0}
            title="Clear selection"
          >
            NONE
          </button>
          <span className={styles.driverChipSpacer} />
          {chipDrivers.map(d => {
            const isSel = selected.includes(d.number);
            return (
              <button
                key={d.number}
                className={`${styles.driverChip} ${isSel ? styles.driverChipSelected : ''}`}
                onClick={() => onToggle?.(d.number)}
                style={{
                  borderColor: isSel ? seriesColor(d) : undefined,
                  color: isSel ? seriesColor(d) : undefined,
                }}
                title={d.full_name || d.tla}
              >
                {d.tla || d.number}
              </button>
            );
          })}
        </div>
      </div>

      {selected.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c', fontFamily: "var(--mono)", fontSize: '0.7rem' }}>
          click a driver on the track map, a row in the timing table, or a chip above to view telemetry
        </div>
      ) : (
        <div className={styles.telGrid}>
          <ChannelTile title="Speed (km/h)" dataKey="speed" domain={[0, 360]}
            formatter={v => `${v|0}`} data={combined} selected={selected}
            drivers={drivers} />
          <ChannelTile title="RPM" dataKey="rpm" domain={[0, 13000]}
            formatter={v => `${(v / 1000).toFixed(0)}k`} data={combined} selected={selected}
            drivers={drivers} />
          <ChannelTile title="Gear" dataKey="gear" domain={[0, 8]}
            formatter={v => `${v|0}`} data={combined} selected={selected}
            drivers={drivers} />
          <ChannelTile title="Throttle (%)" dataKey="throttle" domain={[0, 100]}
            formatter={v => `${v|0}`} data={combined} selected={selected}
            drivers={drivers} />
          <ChannelTile title="Brake" dataKey="brake" domain={[0, 100]}
            formatter={v => `${v|0}`} data={combined} selected={selected}
            drivers={drivers} />
          <ChannelTile title="DRS" dataKey="drs" domain={[0, 15]}
            formatter={v => (v >= 10 ? 'ON' : 'off')} data={combined} selected={selected}
            drivers={drivers} />
        </div>
      )}
    </div>
  );
}
